import { Link } from "react-router-dom";
import { Mail, MessageCircle } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { wa } from "@/constants/content";
import { POLICY_CONTACT, POLICY_LINKS } from "@/constants/policies";

function PolicySupport() {
  return (
    <section className="glass rounded-[2rem] p-6 md:p-8 border border-white/8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-3">
            Policy support
          </p>
          <h2 className="font-cabinet text-2xl md:text-3xl font-bold mb-3">
            Need clarification before paying or placing an order?
          </h2>
          <p className="text-sm md:text-[15px] text-zinc-300 leading-relaxed">
            For policy questions, payment clarification, or grievance-related
            communication, reach ThemeStudios on the official support channels.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <a
            href={`mailto:${POLICY_CONTACT.email}`}
            className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 transition-colors hover:border-[#e03c31]/50"
          >
            <div className="flex items-center gap-2 text-[#e03c31]">
              <Mail size={16} />
              <span className="text-[11px] uppercase tracking-[0.22em] font-bold">
                Email
              </span>
            </div>
            <p className="mt-2 text-sm text-white">{POLICY_CONTACT.email}</p>
          </a>
          <a
            href={wa("package")}
            target="_blank"
            rel="noreferrer"
            className="rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 transition-colors hover:border-[#e03c31]/50"
          >
            <div className="flex items-center gap-2 text-[#e03c31]">
              <MessageCircle size={16} />
              <span className="text-[11px] uppercase tracking-[0.22em] font-bold">
                WhatsApp
              </span>
            </div>
            <p className="mt-2 text-sm text-white">{POLICY_CONTACT.phoneDisplay}</p>
          </a>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {POLICY_LINKS.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-[#e03c31]/50 hover:text-white"
          >
            {link.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function PolicyPage({ policy }) {
  usePageMeta({
    title: policy.metaTitle,
    description: policy.description,
    path: policy.path,
  });

  return (
    <div className="pt-28 md:pt-36 pb-20">
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-8 max-w-5xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-5">
              ThemeStudios Legal
            </p>
            <h1 className="h1-display text-white text-[12vw] md:text-[5vw] max-w-[12ch]">
              {policy.title}
            </h1>
            <p className="mt-6 max-w-3xl text-sm md:text-[15px] text-zinc-300 leading-relaxed">
              {policy.description}
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs text-zinc-300">
              <span className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2">
                Last updated: {policy.lastUpdated}
              </span>
              <span className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-2">
                {policy.appliesTo}
              </span>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-[#e03c31]/50 hover:text-white"
              >
                Home
              </Link>
              {POLICY_LINKS.map((link) => {
                const isActive = link.path === policy.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] transition-colors ${
                      isActive
                        ? "bg-[#e03c31] text-white"
                        : "border border-white/10 text-zinc-300 hover:border-[#e03c31]/50 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <aside className="md:col-span-4 space-y-4">
            <div className="glass rounded-[2rem] p-6 border border-white/8">
              <p className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-4">
                Official support
              </p>
              <div className="space-y-4">
                <a
                  href={`mailto:${POLICY_CONTACT.email}`}
                  className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 transition-colors hover:border-[#e03c31]/50"
                >
                  <Mail size={16} className="text-[#e03c31]" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-bold">
                      Email
                    </p>
                    <p className="text-sm text-white">{POLICY_CONTACT.email}</p>
                  </div>
                </a>
                <a
                  href={wa("package")}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 transition-colors hover:border-[#e03c31]/50"
                >
                  <MessageCircle size={16} className="text-[#e03c31]" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 font-bold">
                      WhatsApp
                    </p>
                    <p className="text-sm text-white">{POLICY_CONTACT.phoneDisplay}</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="glass rounded-[2rem] p-6 border border-white/8">
              <p className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-4">
                What these pages cover
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#e03c31]" />
                  <span>Booking, payments, revisions, delivery and legal scope.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#e03c31]" />
                  <span>Cancellation, refund review, duplicate payments and pack handling.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#e03c31]" />
                  <span>How ThemeStudios collects, uses and safeguards enquiry and order data.</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-14 grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-8">
        <aside className="xl:sticky xl:top-28 h-fit">
          <div className="glass rounded-[2rem] p-6 border border-white/8">
            <p className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-4">
              Quick navigation
            </p>
            <ul className="space-y-3">
              {policy.sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-sm text-zinc-300 transition-colors hover:text-white"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="space-y-5">
          {policy.sections.map((section) => (
            <article
              key={section.id}
              id={section.id}
              className="glass rounded-[2rem] p-6 md:p-8 border border-white/8 scroll-mt-32"
            >
              <h2 className="font-cabinet text-2xl md:text-[2rem] font-bold">
                {section.title}
              </h2>

              <div className="mt-4 space-y-4 text-sm md:text-[15px] leading-relaxed text-zinc-300">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>

              {section.bullets?.length ? (
                <ul className="mt-5 space-y-3">
                  {section.bullets.map((bullet) => (
                    <li
                      key={bullet}
                      className="flex items-start gap-3 text-sm md:text-[15px] text-zinc-300 leading-relaxed"
                    >
                      <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#e03c31]" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {section.title.endsWith("contact") || section.id === "contact" ? (
                <div className="mt-5 space-y-3 text-sm text-zinc-300">
                  <p>Email: {POLICY_CONTACT.email}</p>
                  <p>Phone / WhatsApp: {POLICY_CONTACT.phoneDisplay}</p>
                  <p>Business base: {POLICY_CONTACT.businessBase}</p>
                </div>
              ) : null}
            </article>
          ))}

          <PolicySupport />
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-10">
        <div className="rounded-[2rem] border border-white/6 bg-white/[0.02] px-6 py-5 text-sm text-zinc-400">
          ThemeStudios publishes these policy pages so visitors can review
          payment, privacy, cancellation, and service rules before confirming a
          brief or making a payment.
        </div>
      </section>
    </div>
  );
}
